import { fetchCachedQuery, useCachedQuery } from "@pustak-lms/ui";
import { lazy, Suspense, useEffect } from "react";
import type { OrganizationListResult, SessionIdentity } from "@pustak-lms/contracts";
import { AppShell, Button, PageHeader, PasswordPage, ProfilePage, PlaceholderPage, StatCard, type NavigationItem } from "@pustak-lms/ui";
import { getCountryName } from "@pustak-lms/utils";
import { Link, Route, Routes, useLocation, useNavigate, type Location } from "react-router-dom";
import { platformApiClient } from "./api";
import { CreateOrganizationPage } from "./CreateOrganizationPage";
import { OrganizationsPage } from "./OrganizationsPage";
const OrganizationAppearancePage = lazy(() => import("./OrganizationAppearancePage").then(module => ({ default: module.OrganizationAppearancePage })));
import { TestingPage } from "./TestingPage";
const QuestionLibraryPage = lazy(() => import("./qbank/QuestionLibraryPage").then(module => ({ default: module.QuestionLibraryPage })));
const QuestionDetailPage = lazy(() => import("./qbank/QuestionDetailPage").then(module => ({ default: module.QuestionDetailPage })));
const QuestionPreviewPage = lazy(() => import("./qbank/QuestionPreviewPage").then(module => ({ default: module.QuestionPreviewPage })));
const QuestionEditorPage = lazy(() => import("./qbank/QuestionEditorPage").then(module => ({ default: module.QuestionEditorPage })));
const QuestionCreationPage = lazy(() => import("./qbank/QuestionCreationPage").then(module => ({ default: module.QuestionCreationPage })));
const CaseStudyLibraryPage = lazy(() => import("./case-studies/CaseStudyLibraryPage").then(module => ({ default: module.CaseStudyLibraryPage })));
const CaseStudyEditorPage = lazy(() => import("./case-studies/CaseStudyEditorPage").then(module => ({ default: module.CaseStudyEditorPage })));
const ExamsPage = lazy(() => import("./exams/ExamsPage").then(module => ({ default: module.ExamsPage })));
const AuditPage = lazy(() => import("./AuditPage").then(module => ({ default: module.AuditPage })));
const CreateExamPage = lazy(() => import("./exams/CreateExamPage").then(module => ({ default: module.CreateExamPage })));

const ExamTaxonomyPage = lazy(() => import("./exams/ExamTaxonomyPage").then(module => ({ default: module.ExamTaxonomyPage })));

interface DashboardProps {
  overview: OrganizationListResult | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

function Dashboard({ overview, loading, error, retry }: DashboardProps) {
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        eyebrow="Internal operations"
        title="Platform control center"
        description="Create organizations and keep their owner contacts easy to find."
        actions={<Button onClick={() => navigate("/organizations/new")}>Create organization</Button>}
      />

      <section className="stats-grid dashboard-stats" aria-label="Platform summary">
        <StatCard
          label="Organizations"
          value={loading ? "…" : overview ? String(overview.total) : "—"}
          detail="Recorded in the platform directory"
        />
      </section>

      <section className="content-grid">
        <div className="panel">
          <div className="panel-header"><h2>Recently created organizations</h2><Link to="/organizations">View all</Link></div>
          {loading ? <p className="console-empty" role="status">Loading organizations…</p> : null}
          {!loading && error ? (
            <div className="console-empty" role="alert">
              <p>Unable to load the organization summary.</p>
              <Button variant="secondary" onClick={retry}>Retry</Button>
            </div>
          ) : null}
          {!loading && !error && overview?.items.length === 0 ? (
            <p className="console-empty">No organizations yet. Create the first one.</p>
          ) : null}
          {!loading && !error && overview && overview.items.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Organization</th><th>Owner</th><th>Country</th><th>Created</th></tr></thead>
                <tbody>
                  {overview.items.map((organization) => (
                    <tr key={organization.id}>
                      <td><strong>{organization.name}</strong></td>
                      <td>{organization.ownerName}</td>
                      <td>{getCountryName(organization.countryCode)}</td>
                      <td>{new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(organization.createdAt))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <aside className="panel">
          <div className="panel-header"><h2>Organization directory</h2></div>
          <p className="console-muted">Create a record with the owner's email and mobile numbers, then find it in the directory.</p>
          <Link className="button button-secondary" to="/organizations">Open directory</Link>
        </aside>
      </section>
    </>
  );
}

export function App({ identity }: { identity: SessionIdentity }) {
  const location = useLocation();
  const backgroundLocation = /^\/questions\/[^/]+\/preview$/.test(location.pathname)
    ? (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation : undefined;
  const overviewQuery = useCachedQuery(['organizations', { limit: 3 }], () => platformApiClient().listOrganizations({ limit: 3 }));
  const overview = overviewQuery.data ?? null;
  const loading = overviewQuery.isPending;
  const error = overviewQuery.isError;
  useEffect(() => {
    // App stays mounted between routes. Revisit expired summaries while keeping
    // fresh navigation returns free of equivalent requests.
    void fetchCachedQuery(['organizations', { limit: 3 }], () => platformApiClient().listOrganizations({ limit: 3 })).catch(() => {});
  }, [location.key]);

  const navigation: NavigationItem[] = [
    { label: "Overview", icon: "overview", href: "/" },
    { label: "Organizations", icon: "organizations", href: "/organizations", marker: overview ? String(overview.total) : undefined },
    { label: "Exams", icon: "assignments", href: "/exams", children: [
      { label: "View exams", icon: "assignments", href: "/exams" },
      { label: "Create exam", icon: "assignments", href: "/exams/create" }
    ] },
    { label: "Question bank", icon: "questions", href: "/questions", children: [
      { label: "View questions", icon: "question-list", href: "/questions" },
      { label: "Create questions", icon: "question-create", href: "/questions/create" },
      { label: "View case studies", icon: "case-studies", href: "/case-studies" }
    ] },
    { label: "Testing", icon: "testing", href: "/testing" },
    { label: "Domains", icon: "domains", href: "/domains" },
    { label: "Feature access", icon: "features", href: "/features" },
    { label: "Operations", icon: "operations", href: "/operations" },
    { label: "Audit events", icon: "audit", href: "/audit" }
  ];

  return (<>
    <AppShell
      appName="Platform Admin"
      navigation={navigation}
      activeHref={location.pathname.startsWith("/case-studies") ? "/case-studies" : location.pathname === "/questions/create" || location.pathname === "/questions/new" ? "/questions/create" : location.pathname.startsWith("/questions") ? "/questions" : location.pathname.startsWith("/exams/") && location.pathname !== "/exams/create" ? "/exams" : location.pathname.startsWith("/organizations") ? "/organizations" : location.pathname}
      user={{ name: identity.user.name, role: "Platform Admin" }}
    >
      <Suspense fallback={<p className="console-empty" role="status">Loading workspace…</p>}><Routes location={backgroundLocation ?? location}>
        <Route path="/change-password" element={<PasswordPage kind="change" surface="platform" embedded />} />
        <Route path="/profile" element={<ProfilePage identity={identity} surface="platform" />} />
        <Route path="/" element={<Dashboard overview={overview} loading={loading} error={error} retry={() => void overviewQuery.refetch()} />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/organizations/new" element={<CreateOrganizationPage />} />
        <Route path="/organizations/:organizationId/appearance" element={<OrganizationAppearancePage key={location.pathname} />} />
        <Route path="/exams" element={<ExamsPage />} />
        <Route path="/exams/create" element={<CreateExamPage />} />
        <Route path="/exams/:examId/taxonomy" element={<ExamTaxonomyPage key={location.pathname} />} />
        <Route path="/questions" element={<QuestionLibraryPage />} />
        <Route path="/questions/create" element={<QuestionCreationPage />} />
        <Route path="/questions/new" element={<QuestionEditorPage />} />
        <Route path="/questions/:questionId" element={<QuestionDetailPage />} />
        <Route path="/questions/:questionId/edit" element={<QuestionEditorPage />} />
        <Route path="/questions/:questionId/preview" element={<QuestionPreviewPage />} />
        <Route path="/case-studies" element={<CaseStudyLibraryPage />} />
        <Route path="/case-studies/new" element={<CaseStudyEditorPage key={location.pathname} />} />
        <Route path="/case-studies/:caseStudyId/edit" element={<CaseStudyEditorPage key={location.pathname} />} />
        <Route path="/testing" element={<TestingPage />} />
        <Route path="/domains" element={<PlaceholderPage title="Custom domains" description="DNS validation, SSL state, and hostname mappings will be visible here." />} />
        <Route path="/features" element={<PlaceholderPage title="Feature access" description="Plan entitlements and time-limited overrides will be managed here." />} />
        <Route path="/operations" element={<PlaceholderPage title="Operations" description="Job failures, retries, imports, and system health will live here." />} />
        <Route path="/audit" element={<AuditPage />} />
      </Routes></Suspense>
    </AppShell>
    {backgroundLocation && <Suspense fallback={<p role="status">Loading preview…</p>}><Routes><Route path="/questions/:questionId/preview" element={<QuestionPreviewPage />} /></Routes></Suspense>}
  </>);
}
